import { io, type Socket } from 'socket.io-client';
import { REALTIME_ENGINE_IO_PATH, REALTIME_NAMESPACE } from '@magimp/shared-realtime';
import { useAuthStore } from '../stores/auth.js';

/**
 * @aiPurpose process-singleton Socket.IO client for the realtime channel
 *   (#316). Connects on first `getRealtimeSocket()` call with the
 *   current `useAuthStore.accessToken`; disconnects automatically when
 *   the auth store transitions the token to null (logout / session
 *   reset / forced clear).
 * @aiInvariant the access token NEVER lands in localStorage. The
 *   Zustand store keeps it in memory only, and we read it on
 *   demand for the handshake `auth: { token }` field. Reconnect
 *   attempts use the latest token in the store, so a refreshed JWT
 *   propagates without an explicit reconnect call.
 * @aiSecurity bound to the server-side per-emit permission filter — a
 *   socket whose role lacks `requests.read` simply receives no
 *   `requests:updated` events. The client-side schema validator in
 *   `RealtimeBootstrap` is defense in depth against gateway bugs.
 */

// WHY: same-origin by default — the dev Vite proxy forwards `/realtime`
// to the backend (with `ws: true`) and prod Caddy reverse-proxies the
// same path. Setting `VITE_BACKEND_URL` overrides for non-proxied dev
// or alternate environments.
const BACKEND_URL = (import.meta.env.VITE_BACKEND_URL ?? '') as string;

let socket: Socket | null = null;
let unsubscribe: (() => void) | null = null;
let hasConnectedBefore = false;

// WHY (#1182): Playwright e2e specs that depend on a realtime emit reaching
//   a second browser context (e.g. `box-groups-logist-edit.spec.ts`) must
//   gate the mutation on the observer's socket actually being connected.
//   Without this gate the editor's save races against the observer's
//   Socket.IO handshake and the gateway emit lands before the observer
//   has a subscription — the event is lost and the assertion times out.
//
// The signal is `window.__magimpRealtime.connected: boolean`, kept in sync
//   with `socket.on('connect' | 'disconnect')`. Naming follows the `__`
//   test-bridge convention from `__setHasConnectedBefore` above. It carries
//   no token, no PII, no role; it mirrors `socket.connected` which is
//   already inspectable via DevTools. The shape is also DOM-friendly so
//   `page.waitForFunction(() => window.__magimpRealtime?.connected)` works
//   without `as any` casts in test code.
declare global {
  interface Window {
    __magimpRealtime?: { connected: boolean };
  }
}

function setRealtimeWindowSignal(connected: boolean): void {
  if (typeof window === 'undefined') return;
  window.__magimpRealtime = { connected };
}

export function getRealtimeSocket(): Socket {
  if (socket) return socket;
  // INVARIANT: namespace + path are imported from `@magimp/shared-realtime`
  //   so the gateway and the client cannot drift. A typo on either side
  //   fails the import, not the runtime handshake.
  socket = io(`${BACKEND_URL}${REALTIME_NAMESPACE}`, {
    transports: ['websocket', 'polling'],
    path: REALTIME_ENGINE_IO_PATH,
    autoConnect: true,
    reconnection: true,
    reconnectionDelay: 500,
    reconnectionDelayMax: 30_000,
    // INVARIANT: callback form (not static `auth: { token }`) so every
    //   (re)connect attempt reads the LATEST in-memory access token
    //   from the auth store. After a silent refresh the next reconnect
    //   uses the fresh JWT; without this the socket would keep
    //   re-presenting the stale token captured at first construction
    //   and the gateway's exp-driven disconnect would cycle into a
    //   reconnect loop on the same expired credential.
    auth: (cb: (data: { token: string }) => void) => {
      const t = useAuthStore.getState().accessToken;
      cb({ token: t ?? '' });
    },
    withCredentials: true,
  });

  // Tracks whether the socket has ever completed a successful connection
  // so `RealtimeBootstrap` can distinguish first-ever `connect_error`
  // from one after a prior successful connect.
  socket.on('connect', () => {
    hasConnectedBefore = true;
    setRealtimeWindowSignal(true);
  });
  socket.on('disconnect', () => {
    setRealtimeWindowSignal(false);
  });
  setRealtimeWindowSignal(false);

  // INVARIANT: a transition `accessToken: non-null → null` means the
  //   user has logged out (or the session was forcibly cleared). The
  //   socket must be torn down on the same edge so an orphaned
  //   connection cannot continue receiving events under a now-cleared
  //   identity.
  unsubscribe = useAuthStore.subscribe((state, prev) => {
    if (prev.accessToken && !state.accessToken) {
      disconnectRealtimeSocket();
    }
  });

  return socket;
}

export function disconnectRealtimeSocket(): void {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  if (socket) {
    socket.removeAllListeners();
    socket.io.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  hasConnectedBefore = false;
  setRealtimeWindowSignal(false);
}

/**
 * @aiPurpose returns whether the socket has ever completed a successful
 *   connection in its current lifecycle. Used by the reconnect catch-up
 *   to decide whether `connect_error` should trigger a token refresh.
 * @aiInvariant always false after `disconnectRealtimeSocket()` (logout)
 *   or module reload; true after the first `connect` event on a fresh
 *   `getRealtimeSocket()` call. Never resets to false outside of
 *   `disconnectRealtimeSocket`.
 */
export function getHasConnectedBefore(): boolean {
  return hasConnectedBefore;
}

/** Test-only helper. Sets the has-connected-before flag for tests. */
export function __setHasConnectedBefore(v: boolean): void {
  if (import.meta.env.MODE !== 'test') {
    throw new Error('__setHasConnectedBefore is test-only');
  }
  hasConnectedBefore = v;
}

/** Test-only helper. Resets module-level singleton state between cases. */
export function __resetRealtimeSocketForTests(): void {
  disconnectRealtimeSocket();
}
